import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { type AntiCheatController } from "..";
import {
  isFullscreen,
  isFullscreenSupported,
  onFullscreenChange,
  onFullscreenError,
  requestFullscreen,
} from "../fullscreen";

// Tight key-press → fullscreenchange window. The browser emits the change
// event within ~1 frame of an Escape press; 500 ms is generous enough to
// cover slow machines without misattributing later exits as keyboard-driven.
const KEY_ATTRIBUTION_WINDOW_MS = 500;

export interface UseFullscreenReturn {
  /** Mirrors document.fullscreenElement. The Question route renders the
   *  blocking overlay whenever this is false (and `isSupported` is true). */
  isFullscreen: boolean;
  /** True after the candidate has been in fullscreen at least once this
   *  attempt. Switches the overlay copy from "Enter fullscreen mode to
   *  begin" → "Return to fullscreen mode". One-way flip; never resets. */
  hasEverEntered: boolean;
  /** Why the candidate is out of fullscreen right now. `"file_picker"` when
   *  the OS file dialog of a file-upload question took the browser out (EF-11)
   *  — the one exit that is the task itself: the controller dropped its
   *  `fullscreen_exit`, so the overlay may say it is not counted. `null` for
   *  every other exit, and again as soon as fullscreen is back. */
  exitCause: "file_picker" | null;
  /** True iff the platform supports the Fullscreen API. Stable across the
   *  lifetime of the hook. Callers should skip rendering the blocking
   *  overlay when this is false. */
  isSupported: boolean;
  /** Click handler for the overlay's CTA. Synchronous wrt the user gesture;
   *  the requestFullscreen() call banks the activation token before the
   *  await. On rejection (Permissions-Policy, iframe, denied) emits a
   *  `fullscreen_unavailable: rejected` event — the overlay stays shown. */
  enter: () => Promise<void>;
}

/**
 * Watches the document fullscreen state and emits anti-cheat events on every
 * exit. Owns no UI — the consumer decides what to render based on the
 * returned booleans.
 *
 * In the strict-blocking design, every non-fullscreen state on the Question
 * route triggers a blocking overlay. There is no auto-dismiss, no voluntary
 * exit, no user-initiated suppression — every exit is reported to the
 * controller. The one exit it does not record is the OS file dialog's (EF-11,
 * `beginFilePicker`): the overlay still blocks, but `exitCause` lets it say so
 * calmly instead of warning the candidate about something the task caused.
 *
 * @param controllerRef The anticheat controller from `useAntiCheat`. Events
 *   are emitted via `controllerRef.current?.emit(...)` so they batch with
 *   the rest of the anti-cheat pipeline (2 s flush + sendBeacon on unload).
 * @param enabled FEAT-25: false while the test's fullscreen switch is off
 *   (or not known yet). The hook is then inert — no listeners, no events —
 *   and every returned field is stale by design: the consumer gates its
 *   overlay on the SAME flag. It resyncs from the document the moment
 *   `enabled` turns true, which happens after mount on every page load.
 */
export function useFullscreen(
  controllerRef: MutableRefObject<AntiCheatController | null>,
  enabled: boolean,
): UseFullscreenReturn {
  // Mirrors `document.fullscreenElement != null`, kept in React state so the
  // consumer re-renders when the candidate enters/exits fullscreen via any
  // path. Initialised lazily so SSR / jsdom doesn't trip the document probe.
  const [inFullscreen, setInFullscreen] = useState<boolean>(() =>
    typeof document !== "undefined" ? isFullscreen() : false,
  );
  // Seed from the live document state too: the candidate may have entered
  // fullscreen on the "Begin assessment" gesture *before* this route mounted
  // (see Gate.tsx beginAssessment), so we've effectively already entered. This
  // keeps the overlay copy correct ("Return to fullscreen mode") if they later
  // exit. One-way flip thereafter; never resets.
  const [hasEverEntered, setHasEverEntered] = useState<boolean>(() =>
    typeof document !== "undefined" ? isFullscreen() : false,
  );
  // Capability probe. Stable per mount — the underlying API doesn't appear
  // or disappear at runtime, so this is computed once.
  const [isSupported] = useState<boolean>(() => isFullscreenSupported());
  const [exitCause, setExitCause] = useState<"file_picker" | null>(null);

  // Mutable refs so the listeners (mounted once per `enabled` flip) always see
  // the current controller and the latest Escape timestamp without re-binding.
  const controllerRefRef = useRef(controllerRef);
  controllerRefRef.current = controllerRef;
  const lastEscapeAtRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    // The mount-time snapshot may be stale: `enabled` can turn true after
    // mount (the switch arrives with the question payload), and fullscreen
    // may have changed while nothing was listening.
    const live = isFullscreen();
    setInFullscreen(live);
    if (live) setHasEverEntered(true);

    // Best-effort method attribution. We can't reliably distinguish F11 from
    // OS-chrome (Win+D, Mission Control), so both fall through to "unknown".
    // - Escape: keydown listener bumps the timestamp; if a change event fires
    //   within KEY_ATTRIBUTION_WINDOW_MS, attribute as "escape".
    // - Blur: document.visibilityState === "hidden" at the moment of exit
    //   (typical for OS-level switches like Alt-Tab that also release FS).
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        lastEscapeAtRef.current = Date.now();
      }
    };

    const deriveMethod = (): "escape" | "blur" | "unknown" => {
      if (Date.now() - lastEscapeAtRef.current < KEY_ATTRIBUTION_WINDOW_MS) {
        return "escape";
      }
      if (document.visibilityState === "hidden") {
        return "blur";
      }
      return "unknown";
    };

    const handleChange = (): void => {
      const nowInFullscreen = isFullscreen();
      setInFullscreen(nowInFullscreen);
      if (nowInFullscreen) {
        // Entered. Flip the one-way "has ever entered" flag so the overlay
        // copy switches from "Enter" → "Return" on the next exit.
        setHasEverEntered(true);
        setExitCause(null);
        return;
      }
      // Exited. Always report — there is no voluntary-exit suppression in the
      // strict-blocking model. The consumer's render gates on
      // `inFullscreen === false` and shows the blocking overlay.
      //
      // The cause is read from the SAME predicate `emit` drops on, in the same
      // tick, so the overlay's "not counted against you" is true exactly when
      // the event below is not recorded — never a promise the timeline breaks.
      const controller = controllerRefRef.current.current;
      setExitCause(controller?.isFilePickerActive() ? "file_picker" : null);
      controller?.emit({
        type: "fullscreen_exit",
        method: deriveMethod(),
      });
    };

    // Safari resolves a REFUSED prefixed request (it returns undefined, not a
    // rejecting promise), so `enter()`'s catch never runs there and the refusal
    // is invisible. The error event is the only signal — surface it through the
    // same payload `enter()` would have emitted, so the recruiter can see why
    // the candidate is stuck behind the overlay.
    const handleError = (): void => {
      controllerRefRef.current.current?.emit({
        type: "fullscreen_unavailable",
        reason: "rejected",
      });
    };

    const unsubscribe = onFullscreenChange(handleChange);
    const unsubscribeError = onFullscreenError(handleError);
    // Capture phase so Monaco can't swallow Escape before we see it.
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      unsubscribe();
      unsubscribeError();
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [enabled]);

  const enter = useCallback(async (): Promise<void> => {
    // Click handler is the fresh user gesture; the API will grant under
    // normal conditions. No await between the gesture and the call.
    try {
      await requestFullscreen();
      // handleChange will fire on the resulting fullscreenchange and flip
      // both inFullscreen + hasEverEntered. Nothing else to do here.
    } catch {
      // The user gesture was real but the request still rejected (iframe,
      // permissions-policy, etc.). Surface the unavailability so the
      // recruiter can see it; the overlay stays visible because
      // inFullscreen stays false.
      controllerRefRef.current.current?.emit({
        type: "fullscreen_unavailable",
        reason: "rejected",
      });
    }
  }, []);

  return {
    isFullscreen: inFullscreen,
    hasEverEntered,
    exitCause,
    isSupported,
    enter,
  };
}
